import React from 'react';
import { useCurrentFrame, interpolate } from 'remotion';

const COLORS = {
  bg: '#0f172a',
  textMuted: '#94a3b8',
  green: '#059669',
  red: '#dc2626',
};

// Realistic CRE property data (5 rows - row index 1 is unavailable)
const PROPERTIES = [
  { address: '4521 Commerce Way', city: 'Augusta', sf: '32,000', rent: '$4.25', nnn: '$1.15', docks: '4', height: "24'", power: '400A', flyer: 'View PDF', comments: 'Recently renovated, new roof 2024', unavailable: false },
  { address: '320 Warehouse Ln', city: 'Evans', sf: '', rent: '', nnn: '', docks: '', height: '', power: '', flyer: '', comments: 'Broker confirmed property leased', unavailable: true },
  { address: '890 Distribution Blvd', city: 'Evans', sf: '45,000', rent: '$4.50', nnn: '$1.20', docks: '6', height: "28'", power: '800A', flyer: 'View PDF', comments: 'Rail spur access, fenced yard', unavailable: false },
  { address: '2100 Logistics Dr', city: 'Augusta', sf: '58,000', rent: '$3.95', nnn: '$1.35', docks: '8', height: "32'", power: '1200A', flyer: 'View PDF', comments: 'Cross-dock capable, ESFR sprinklers', unavailable: false },
  { address: '1750 Industrial Pkwy', city: 'Augusta', sf: '24,500', rent: '$5.10', nnn: '$0.95', docks: '3', height: "22'", power: '200A', flyer: 'View PDF', comments: 'Flexible lease terms available', unavailable: false },
];

const COLUMNS = ['Property', 'City', 'SF', 'Rent', 'NNN', 'Docks', 'Ht', 'Power', 'Flyer', 'Comments'];

/**
 * SpreadsheetMockup - Wide horizontal spreadsheet for vertical layout
 */
const SpreadsheetMockup = ({ filledRows = 0, dividerProgress = 0, style }) => {
  const showDivider = dividerProgress > 0;
  // Total: 1165px wide - all columns fully visible
  // Property(200) City(85) SF(80) Rent(65) NNN(65) Docks(55) Ht(45) Power(75) Flyer(100) Comments(295)
  const gridCols = '200px 85px 80px 65px 65px 55px 45px 75px 100px 295px';

  return (
    <div
      style={{
        background: '#ffffff',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        width: 1165,
        ...style,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridCols,
          background: '#f1f5f9',
          borderBottom: '2px solid #e2e8f0',
          borderRadius: '12px 12px 0 0',
        }}
      >
        {COLUMNS.map((col, i) => (
          <div
            key={i}
            style={{
              padding: '14px 8px',
              fontSize: 16,
              fontWeight: 600,
              color: '#475569',
              textAlign: 'center',
              borderRight: '1px solid #e2e8f0',
            }}
          >
            {col}
          </div>
        ))}
      </div>

      {/* Data Rows - viable properties only (unavailable ones move below divider) */}
      {PROPERTIES.map((prop, rowIndex) => {
        // Skip unavailable properties in main list when divider is showing
        if (prop.unavailable && showDivider) return null;

        // For filling, count only viable rows before this one
        const viableRowsBefore = PROPERTIES.slice(0, rowIndex).filter(p => !p.unavailable).length;
        const isFilled = !prop.unavailable && viableRowsBefore < filledRows;

        // Fade out unavailable row as divider appears
        const rowOpacity = prop.unavailable ? (1 - dividerProgress) : 1;

        return (
          <div
            key={rowIndex}
            style={{
              display: 'grid',
              gridTemplateColumns: gridCols,
              borderBottom: '1px solid #e2e8f0',
              background: isFilled ? 'rgba(5, 150, 105, 0.08)' : 'transparent',
              opacity: rowOpacity,
            }}
          >
            {/* Property address - always shows */}
            <div
              style={{
                padding: '14px 8px',
                fontSize: 16,
                color: isFilled ? COLORS.green : '#1e293b',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                borderRight: '1px solid #e2e8f0',
              }}
            >
              {prop.address}
            </div>
            {/* Data fields */}
            {['city', 'sf', 'rent', 'nnn', 'docks', 'height', 'power', 'flyer', 'comments'].map((field, i) => (
              <div
                key={field}
                style={{
                  padding: '14px 8px',
                  fontSize: 16,
                  textAlign: (field === 'comments' && isFilled) ? 'left' : 'center',
                  color: isFilled ? (field === 'flyer' ? '#3b82f6' : COLORS.green) : '#94a3b8',
                  borderRight: i < 8 ? '1px solid #e2e8f0' : 'none',
                  fontWeight: isFilled ? 500 : 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textDecoration: isFilled && field === 'flyer' ? 'underline' : 'none',
                }}
              >
                {isFilled ? prop[field] : '—'}
              </div>
            ))}
          </div>
        );
      })}

      {/* NON-VIABLE Divider */}
      {showDivider && (
        <div
          style={{
            height: 44,
            background: `rgba(220, 38, 38, ${dividerProgress * 0.15})`,
            borderTop: `3px solid rgba(220, 38, 38, ${dividerProgress})`,
            borderBottom: `3px solid rgba(220, 38, 38, ${dividerProgress})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: '0.05em',
            color: `rgba(139, 0, 0, ${dividerProgress})`,
            opacity: dividerProgress,
          }}
        >
          NON-VIABLE
        </div>
      )}

      {/* Unavailable Property Rows - appear below divider */}
      {showDivider && PROPERTIES.filter(p => p.unavailable).map((prop, i) => (
        <div
          key={`unavailable-${i}`}
          style={{
            display: 'grid',
            gridTemplateColumns: gridCols,
            borderBottom: '1px solid #e2e8f0',
            borderRadius: '0 0 12px 12px',
            opacity: dividerProgress,
            background: `rgba(245, 245, 245, ${dividerProgress * 0.5})`,
          }}
        >
          <div
            style={{
              padding: '14px 8px',
              fontSize: 16,
              color: '#9ca3af',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              borderRight: '1px solid #e2e8f0',
            }}
          >
            {prop.address}
          </div>
          <div
            style={{
              padding: '14px 8px',
              fontSize: 16,
              textAlign: 'center',
              color: '#9ca3af',
              borderRight: '1px solid #e2e8f0',
            }}
          >
            {prop.city}
          </div>
          {/* Empty cells for unavailable property (7 empty, then comment) */}
          {[0, 1, 2, 3, 4, 5, 6].map((j) => (
            <div
              key={j}
              style={{
                padding: '14px 8px',
                fontSize: 16,
                textAlign: 'center',
                color: '#9ca3af',
                borderRight: '1px solid #e2e8f0',
              }}
            >
              —
            </div>
          ))}
          {/* Comment cell */}
          <div
            style={{
              padding: '14px 8px',
              fontSize: 16,
              textAlign: 'left',
              color: '#9ca3af',
              fontStyle: 'italic',
            }}
          >
            {prop.comments}
          </div>
        </div>
      ))}

    </div>
  );
};

/**
 * HowItWorksAnimation - VERTICAL LAYOUT
 *
 * Step 1 on top, arrow in middle, Step 2 on bottom
 * Composition: 1000x1200
 */
export const HowItWorksAnimation = () => {
  const frame = useCurrentFrame();

  const isSettled = frame >= 270;

  const arrowProgress = isSettled
    ? 1
    : interpolate(frame, [0, 60], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  const step2Opacity = isSettled
    ? 1
    : interpolate(frame, [60, 90], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  const filledRows = isSettled
    ? 4
    : Math.floor(
        interpolate(frame, [90, 210], [0, 4], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
      );

  const dividerProgress = isSettled
    ? 1
    : interpolate(frame, [210, 270], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: COLORS.bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 30,
        padding: '40px 50px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {/* Step 1: Upload - Always visible */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 30,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            minWidth: 120,
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 28,
              fontWeight: 700,
            }}
          >
            1
          </div>
          <div style={{ color: COLORS.textMuted, fontSize: 18, fontWeight: 500, textAlign: 'center' }}>
            You upload an<br />empty scrub
          </div>
        </div>
        <SpreadsheetMockup filledRows={0} dividerProgress={0} />
      </div>

      {/* Arrow - Points down, animates in frames 0-60 */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 12,
          opacity: arrowProgress,
        }}
      >
        <div style={{ color: COLORS.textMuted, fontSize: 18, fontWeight: 500, textAlign: 'center' }}>
          AI handles all conversations
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div
            style={{
              width: 3,
              height: arrowProgress * 60,
              background: 'linear-gradient(180deg, #3b82f6, #8b5cf6)',
            }}
          />
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8b5cf6"
            strokeWidth="2.5"
            style={{ marginTop: -2 }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* Step 2: Receive - Fades in frames 60-90 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 30,
          opacity: step2Opacity,
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            minWidth: 120,
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 28,
              fontWeight: 700,
            }}
          >
            2
          </div>
          <div style={{ color: COLORS.textMuted, fontSize: 18, fontWeight: 500, textAlign: 'center' }}>
            You receive
          </div>
        </div>
        <SpreadsheetMockup filledRows={filledRows} dividerProgress={dividerProgress} />
      </div>
    </div>
  );
};

export default HowItWorksAnimation;
